// Deployment config. Two layers (owner decision 2026-09-29, legacy-migration
// RULES.md "előélesítés ideiglenes címeken": the SAME deployment first runs as
// beta.grabbitbot.com -> beta-api.grabbitbot.com, and the cutover to
// secure.grabbitbot.com -> api.grabbitbot.com must need no rebuild):
//
// 1. RUNTIME: `/runtime-config.js` sets `window.__GRABBITBOT_PORTAL_CONFIG__`
//    before the bundle runs. In the Docker image entrypoint.sh writes it from
//    the container environment (PORTAL_API_BASE_URL, PORTAL_SPOTBOT_PUBLIC_IP —
//    validated, refused when missing); the cutover edits the host environment
//    file and restarts. In `vite dev` / `vite preview` the static
//    public/runtime-config.js sets nothing.
// 2. BUILD: the Vite env vars (`.env.example`), then the production default.
//
// `||`, not `??`: docker-compose passes every unset build arg as an EMPTY
// string, and an empty value must mean "use the next layer".

export interface RuntimeConfig {
  apiBaseUrl?: string
  spotbotPublicIp?: string
}

export interface BuildEnv {
  VITE_API_BASE_URL?: string
  VITE_AUTH0_LOGIN_URL?: string
  VITE_AUTH0_LOGIN_ORIGIN?: string
}

export interface ResolvedConfig {
  apiBaseUrl: string
  auth0LoginUrl: string
  auth0LoginExpectedOrigin: string
  spotbotPublicIp: string
}

export const DEFAULT_API_BASE_URL = 'https://api.grabbitbot.com'

/** Legacy egress address of the GCP spotbot — only the dev/preview fallback;
 * every Docker deployment sets PORTAL_SPOTBOT_PUBLIC_IP (entrypoint.sh refuses
 * to start without it), see ticket d3d84751. */
export const LEGACY_SPOTBOT_PUBLIC_IP = '35.230.129.241'

export function resolveConfig(runtime: RuntimeConfig | undefined, env: BuildEnv): ResolvedConfig {
  const rt = runtime ?? {}
  const apiBaseUrl = (rt.apiBaseUrl || env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL).replace(/\/+$/, '')
  // A runtime API URL wins over the build-time login overrides: those would
  // otherwise keep pointing the login popup at the name the image was built for.
  const auth0LoginUrl = (rt.apiBaseUrl ? '' : env.VITE_AUTH0_LOGIN_URL) || `${apiBaseUrl}/user/login/auth0`
  const auth0LoginExpectedOrigin =
    (rt.apiBaseUrl ? '' : env.VITE_AUTH0_LOGIN_ORIGIN) || new URL(apiBaseUrl).origin
  return {
    apiBaseUrl,
    auth0LoginUrl,
    auth0LoginExpectedOrigin,
    spotbotPublicIp: rt.spotbotPublicIp || LEGACY_SPOTBOT_PUBLIC_IP,
  }
}

declare global {
  interface Window {
    __GRABBITBOT_PORTAL_CONFIG__?: RuntimeConfig
  }
}

const resolved = resolveConfig(
  typeof window !== 'undefined' ? window.__GRABBITBOT_PORTAL_CONFIG__ : undefined,
  import.meta.env,
)

/** Backend base URL — legacy `environment.apiUrl`
 * (`https://api-grabbitbot-com.appspot.com` in prod), now the FastAPI rewrite:
 * beta-api.grabbitbot.com on staging, api.grabbitbot.com after the cutover. */
export const API_BASE_URL: string = resolved.apiBaseUrl

/** Auth0 login popup entry point — `GET /user/login/auth0` (owner decision
 * 2026-09-26, legacy-migration/RULES.md: end-user login moved to Auth0's
 * Universal Login, same contract the screener already ships). Passed to
 * `Auth0LoginButton`, or through `buildAuth0PopupUrl` for the
 * `?signup=1`/`?connection=...` variants (register page, "Continue with
 * Google"). This ALSO replaces the legacy unvalidated-origin Google
 * `postMessage` handler (audit §10 defect 1, SECURITY-HIGH) — there is no
 * direct-Google popup in this app at all, only the Auth0 flow, which
 * `Auth0LoginButton` already origin-checks. */
export const AUTH0_LOGIN_URL: string = resolved.auth0LoginUrl

/** Origin the Auth0-login popup's `postMessage` reply must come from — the
 * fix for audit §10 defect 1. Defaults to the API's own origin, which is
 * correct as long as the popup is served from the backend itself (as it is
 * in the legacy contract). */
export const AUTH0_LOGIN_EXPECTED_ORIGIN: string = resolved.auth0LoginExpectedOrigin

/** App build version, injected by Vite from package.json at build time
 * (see vite.config.ts `define`). Used by the static /health payload. */
export const APP_VERSION: string = __APP_VERSION__

/** Where a successful Auth0 popup login/registration sends the user next.
 * Legacy had no real post-login landing concept either (§3: no redirect
 * anywhere on auth-state change) — round 1 picks the dashboard, the first
 * page every legacy route table put a bare `''` redirect at (audit §3a,
 * last row: `'' -> /dashboard`). */
export const POST_LOGIN_PATH = '/dashboard'

/** Role string `RequireAuth`'s `requireRole` checks for admin-only pages.
 * The legacy app had NO client-side role check at all — `User.roles` was
 * defined but never read anywhere (audit §3/§10 defect 2): every
 * `/admin/*` route was reachable by any authenticated client, and access
 * control existed only (if at all) on the unaudited PHP backend. This is a
 * genuine rewrite addition, not a migration (audit §11 "Auth/authorization
 * is a genuine gap to close, not carry over") — the rewritten backend is
 * expected to put a real `"admin"` role in `GET /user/current`'s `roles`
 * array; the backend remains the actual authorization boundary either way,
 * this guard is UX only. */
export const ADMIN_ROLE = 'admin'

/** Placeholder exchange-account scope for nav links that need SOME
 * `:exchangeId/:tradingType` pair to point at (trading-config, statistics).
 * Legacy's own header hardcoded the same kind of default (audit §3a: the
 * screener's `HeaderComponent` always resolved to `binance`/`spot`); this
 * portal's real exchange-account picker does not exist yet in round 1 (the
 * `/settings/exchange` page below is still a placeholder) — see README
 * "Known gaps" / the open ticket for a real account-scoped nav. */
export const DEFAULT_EXCHANGE_ID = 'default'
export const DEFAULT_TRADING_TYPE = 'spot'

/** Address users whitelist on their exchange API key — shown on both
 * settings pages (legacy `v3-settings-default`/`v3-settings-exchange`
 * templates hardcoded it). It is the egress address of the SPOTBOT, which
 * places the orders — the prod host once the spotbot moves off GCP, so it is
 * runtime configuration (PORTAL_SPOTBOT_PUBLIC_IP), ticket d3d84751. Not a
 * secret. */
export const SPOTBOT_PUBLIC_IP: string = resolved.spotbotPublicIp
